import { type ReactElement } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { PlanView } from '@/components/wizard/plan/plan-view';
import { isPinnedDraft } from '@/components/wizard/shared/wizard-steps';
import { wizardDraftQueryOptions } from '@/hooks/data/use-wizard';
import { loadNamespaces } from '@/lib/i18n/i18n';

const paramsSchema = z.object({
  strategyId: z.coerce.number().int().positive(),
});

export const Route = createFileRoute('/_authenticated/wizard/plan/$strategyId')(
  {
    params: {
      parse: (raw) => paramsSchema.parse(raw),
      stringify: ({ strategyId }) => ({ strategyId: String(strategyId) }),
    },
    staticData: {
      title: 'route.wizard_plan.title',
      messageContext: { context_type: 'wizard', idParam: 'strategyId' },
    },
    // A pinned draft has no plan page; a draft that fails to load is left to PlanView's error states.
    beforeLoad: async ({ context, params }) => {
      const draft = await context.queryClient
        .ensureQueryData(wizardDraftQueryOptions(params.strategyId))
        .catch(() => null);
      if (draft && isPinnedDraft(draft)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: '/wizard/campaigns/$strategyId',
          params: { strategyId: params.strategyId },
          replace: true,
        });
      }
    },
    loader: () => loadNamespaces('goals'),
    component: PlanRoute,
  },
);

function PlanRoute(): ReactElement {
  const { strategyId } = Route.useParams();
  return <PlanView strategyId={strategyId} />;
}
